const CLEANUP_TIMER_KEY = '__builderAutosaveLastCleanup';
const QUOTA_EXCEEDED_ERROR = 'QuotaExceededError';
const OLD_AUTOSAVE_PREFIX = 'builderAutosave___';
const AUTOSAVE_PREFIX = 'RbuilderAutosave___';
const NEW_FORM_ID = '_NEW_';
const SAVE_LIMIT = 4;

class AutoSave {
  constructor() {
    this.state = null;
    this.cachedResponse = null;
    this.supported = true;
    this.formId = null;
    this.pendingSave = 0;
    this.key = null;
    this.dummyResolve = null;
    this.saveInterval = null;
    this.response = null;
  }

  setupAutosave = (state, id) => {
    this.formId = id || NEW_FORM_ID;
    this.key = `${AUTOSAVE_PREFIX}${this.formId}`;
    this.state = state;
    return this;
  };

  checkIfSupported = (state, id) => {
    let storage;
    const type = 'localStorage';
    const testString = '__storage_test__';
    try {
      storage = window[type];
      storage.setItem(testString, testString);
      storage.removeItem(testString);
    } catch (e) {
      this.supported = this.confirmUnsupported(e, storage);
    }
    if (this.supported) {
      this.setupAutosave(state, id);
      this.setupSaveInterval();
    }
    return this.supported;
  };

  setupSaveInterval = () => {
    const interval = 3000;
    if (this.saveInterval) clearInterval(this.saveInterval);
    this.saveInterval = setInterval(() => {
      if (this.pendingSave) this.saveResponse(this.cachedResponse);
    }, interval);
  };

  saveResponse = (response, key = this.key) => {
    if (!this.supported) return;

    try {
      localStorage.setItem(key, JSON.stringify(response));
      this.pendingSave = 0;
    } catch (error) {
      if (error.name === QUOTA_EXCEEDED_ERROR) {
        console.warn('LocalStorage is full, consider freeing up space.');
      } else {
        console.error('Error saving to localStorage:', error);
      }
    }
  };

  confirmUnsupported = (e, storage) => {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === QUOTA_EXCEEDED_ERROR ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  };

  save2LocalStorage = (cachedResponse) => {
    this.pendingSave += 1;
    if (this.pendingSave >= SAVE_LIMIT) this.saveResponse(cachedResponse);
  };

  saveForm = (form) => {
    this.cachedResponse = form.present;
    this.save2LocalStorage(this.cachedResponse);
  };

  getSavedResponse = async (askConfirmation = false, id) => {
    if (!this.supported) {
      return { success: false, response: null, needToConfirm: false };
    }

    this.formId = id || NEW_FORM_ID;
    this.key = `${AUTOSAVE_PREFIX}${this.formId}`;
    const stringedResponse = localStorage.getItem(this.key);
    const response = stringedResponse ? JSON.parse(stringedResponse) : null;
    this.response = response;

    const hasResponse = this.checkEmptyForm(response);
    const needToConfirm = hasResponse && askConfirmation;

    return { success: true, response, needToConfirm };
  };

  deleteSavedResponse = (key = this.key) => {
    if (!this.supported) return;
    localStorage.removeItem(key);
  };

  checkEmptyForm(response) {
    if (!response || !response.builderForm) return null;
    const { builderForm } = response;
    const { pages } = builderForm || {};
    if (!pages || pages.length === 0) return null;
    const firstPage = Object.entries(pages[0]);
    return firstPage.length > 1;
  }

  cleanUpSavedResponses = () => {
    if (!this.supported) return;

    const newFormKey = `${AUTOSAVE_PREFIX}${NEW_FORM_ID}` || `${OLD_AUTOSAVE_PREFIX}${NEW_FORM_ID}`;
    const lastCleanup = localStorage.getItem(CLEANUP_TIMER_KEY);

    if (lastCleanup) return;

    for (const key of Object.keys(localStorage)) {
      if (key !== newFormKey && (key.startsWith(AUTOSAVE_PREFIX) || key.startsWith(OLD_AUTOSAVE_PREFIX))) {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.error('Error parsing localStorage item:', key, error);
        }
      }
    }

    localStorage.setItem(CLEANUP_TIMER_KEY, Date.now().toString());
  };
}

const autosave = new AutoSave();
export default autosave;
